<template>
  <div
    class="global-chatbot-container"
    :class="{'hidden': isChatBotHidden, 'is-editor': isPresentationEditorRoute($route)}"
    :style="containerStyles"
  >
    <div v-if="hasCloseButton" class="close-button-container">
      <aha-icon-button
        @click="closeChatbot"
        class="close-button"
        v-aha-emit-action
        name="close-chatbot-button"
      >
        <aha-icon
          name="close-v2"
          width="18"
          height="18"
          viewBox="0 0 16 16"
        />
      </aha-icon-button>
    </div>
    <AIChatbotIframe
      :src="aiChatbotUrl"
      :errorComponent="AIChatbotIframeError"
      @closePanel="closeChatbot"
      @onIframeReady="onIframeReady"
    />
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { debounce } from 'lodash'
import AIChatbotIframe from '@/components-v2/aha-ai/AIChatbotIframe.vue'
import AIChatbotIframeError from '@/components-v2/aha-ai/AIChatbotIframeError.vue'

export default {
  name: 'GlobalChatbotContainer',
  components: {
    AIChatbotIframe,
  },
  props: {
    hasCloseButton: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      AIChatbotIframeError,
      dynamicStyles: {
        top: '0px',
        right: '0px',
        width: '0px',
        height: '0px',
      },
      resizeObserver: null,
    }
  },
  computed: {
    ...mapState({
      renderPresentationDetail: (state) => state.renderPresentationDetail,
      isPreview: (state) => state.isPreview,
      isGuestUser: (state) => state.currentUser.isGuest,
      isSharingView: (state) => state.sharePresentation,
    }),

    ...mapGetters(['isPresentationEditorRoute']),
    ...mapGetters('aiChatbot', ['canUseAIChatbot', 'aiChatbotUrl']),
    ...mapGetters('featureToggles', ['canUseAIImprovingSlideContent']),
    ...mapGetters('rightNav', ['isAITabActive']),
    ...mapGetters(['isBackstage', 'isPresenting']),

    isAIAutoPanelVisible() {
      return this.canUseAIImprovingSlideContent && this.isAITabActive
    },

    isRestrictedState() {
      return this.isPresenting || this.isBackstage || this.isSharingView || this.isPreview
    },

    isChatBotHidden() {
      return !this.isAIAutoPanelVisible
        || !this.renderPresentationDetail
        || !this.isPresentationEditorRoute(this.$route)
        || this.isRestrictedState
    },

    containerStyles() {
      if (this.isPresentationEditorRoute(this.$route)) {
        return this.dynamicStyles
      }
      return {}
    },
  },

  watch: {
    isChatBotHidden(newVal) {
      if (!newVal && this.isPresentationEditorRoute(this.$route)) {
        this.$nextTick(() => {
          this.updatePosition()
        })
      }
    },
  },

  mounted() {
    if (this.isPresentationEditorRoute(this.$route)) {
      this.setupPositionSync()
    }
  },

  destroyed() {
    this.teardownPositionSync()
    this.setIsIframeReadyAction(false)
  },

  methods: {
    ...mapActions('rightNav', ['setRightNavActiveTabAction']),
    ...mapActions('aiChatbot', ['setIsIframeReadyAction']),

    closeChatbot() {
      this.setRightNavActiveTabAction('')
    },

    onIframeReady() {
      this.setIsIframeReadyAction(true)
    },

    updatePosition() {
      const aiAutoPanel = document.getElementById('ai-auto-panel')
      if (!aiAutoPanel) return

      const multiTabs = aiAutoPanel.querySelector('.multi-tabs')
      const targetElement = multiTabs || aiAutoPanel

      const rect = targetElement.getBoundingClientRect()
      const parent = this.$el.offsetParent || document.body
      const parentRect = parent.getBoundingClientRect()

      // Calculate exact x & y position of ai-auto-panel relative to parent
      const xPosition = rect.left - parentRect.left
      const yPosition = rect.top - parentRect.top

      this.dynamicStyles = {
        top: `${yPosition}px`,
        left: `${xPosition}px`, // Use left instead of right for exact positioning
        width: `${rect.width}px`,
        height: `${rect.height}px`,
      }
    },

    updatePositionDebounced: debounce(function positionDebounced() {
      this.updatePosition()
    }, 16), // ~60fps

    setupPositionSync() {
      this.$nextTick(() => {
        this.updatePosition()
      })

      const aiAutoPanel = document.getElementById('ai-auto-panel')
      if (aiAutoPanel) {
        this.resizeObserver = new ResizeObserver(this.updatePositionDebounced)

        const multiTabs = aiAutoPanel.querySelector('.multi-tabs')
        if (multiTabs) {
          this.resizeObserver.observe(multiTabs)
        } else {
          this.resizeObserver.observe(aiAutoPanel)
        }
      }

      // Also observe window resize events for viewport changes
      window.addEventListener('resize', this.updatePositionDebounced)
    },

    teardownPositionSync() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect()
        this.resizeObserver = null
      }
      window.removeEventListener('resize', this.updatePositionDebounced)
      if (this.updatePositionDebounced && typeof this.updatePositionDebounced?.cancel === 'function') {
        this.updatePositionDebounced.cancel() // Cancel any pending debounced calls
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variable.scss";
.global-chatbot-container {
  position: fixed;
  bottom: 0;
  right: 20px;
  width: 400px;
  height: 600px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 38px rgba(82, 95, 127, 0.12);
  border: 1px solid #e5e7eb;
  z-index: 9999;

  &.is-editor {
    position: absolute;
    border-radius: 0.5rem;
    border: none;
    box-shadow: none;
    z-index: 1;

    @media (max-width: 960px) {
      z-index: 10;
    }

    @media (max-width: 724px) {
      width: calc(100% - 64px);
      height: 100%;
      border-radius: 0;
    }
  }

  .close-button-container {
    @apply absolute right-0;
    padding: 0 12px;
    top: 8.5px;

    .close-button {
      width: 32px;
      height: 32px;
      color: rgba(0,0,0,0.88);
      &:hover {
        background-color: rgba(0,0,0,0.04);
        color: rgba(0,0,0,0.88);
      }
      &:not(:disabled):focus-visible, &:not(:disabled):focus, &:not(:disabled):active, &:not(:disabled):focus-within {
        outline: 3px solid #91caff;
        outline-offset: 1px;
        transition: outline-offset 0s, outline 0s;
        background-color: #ffffff;
        box-shadow: none;
      }
    }
  }
}
</style>
